// Markdown parsing utilities
import DOMPurify from 'dompurify';
import { marked } from 'marked';

// Configure marked for safe rendering
marked.setOptions({
    breaks: true,
    gfm: true
});

// Parse markdown to HTML
export function parseMarkdown(content) {
    try {
        const parsed = marked.parse(content || '');
        return DOMPurify.sanitize(typeof parsed === 'string' ? parsed : '');
    } catch (e) {
        console.error('Markdown parse error:', e);
        return DOMPurify.sanitize(content || '');
    }
}

// Strip markdown formatting for accurate word counting
function stripMarkdown(text) {
    if (!text) return '';
    return text
        // Remove code blocks (``` ... ```)
        .replace(/```[\s\S]*?```/g, '')
        // Remove inline code (` ... `)
        .replace(/`[^`]+`/g, '')
        // Remove images ![alt](url)
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
        // Remove links but keep text [text](url) -> text
        .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
        // Remove bold **text** or __text__
        .replace(/(\*\*|__)(.*?)\1/g, '$2')
        // Remove italic *text* or _text_
        .replace(/(\*|_)(.*?)\1/g, '$2')
        // Remove strikethrough ~~text~~
        .replace(/~~(.*?)~~/g, '$1')
        // Remove headings # ## ### etc
        .replace(/^#{1,6}\s+/gm, '')
        // Remove blockquotes >
        .replace(/^>\s+/gm, '')
        // Remove horizontal rules
        .replace(/^[-*_]{3,}\s*$/gm, '')
        // Remove list markers - * + 1.
        .replace(/^[\s]*[-*+]\s+/gm, '')
        .replace(/^[\s]*\d+\.\s+/gm, '')
        // Clean up extra whitespace
        .replace(/\n{3,}/g, '\n\n');
}

// Count words in text (strips markdown first for accuracy)
export function countWords(text) {
    if (!text) return 0;
    const stripped = stripMarkdown(text);
    return stripped
        .trim()
        .split(/\s+/)
        .filter(word => word.length > 0).length;
}

// Count characters in text
export function countCharacters(text) {
    if (!text) return 0;
    return text.length;
}

// Estimate reading time (words per minute)
export function estimateReadingTime(text, wpm = 200) {
    const words = countWords(text);
    const minutes = Math.ceil(words / wpm);
    return minutes;
}

// Extract headings from markdown
export function extractHeadings(content) {
    const headings = [];
    const regex = /^(#{1,6})\s+(.+)$/gm;
    let match;

    while ((match = regex.exec(content)) !== null) {
        headings.push({
            level: match[1].length,
            text: match[2],
            position: match.index
        });
    }

    return headings;
}

// Get a summary of the content (first paragraph or first N characters)
export function getSummary(content, maxLength = 150) {
    if (!content) return '';

    // Get first paragraph
    const firstPara = content.split(/\n\n/)[0] || '';

    // Remove markdown formatting
    const cleaned = firstPara
        .replace(/#{1,6}\s+/g, '')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/\[(.+?)\]\(.+?\)/g, '$1')
        .replace(/`(.+?)`/g, '$1')
        .trim();

    if (cleaned.length <= maxLength) return cleaned;
    return cleaned.slice(0, maxLength).trim() + '...';
}

// Generate a slug from text
export function slugify(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export default {
    parseMarkdown,
    countWords,
    countCharacters,
    estimateReadingTime,
    extractHeadings,
    getSummary,
    slugify
};
